import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Download, Trash2, ShieldAlert, FileArchive, Lock } from "lucide-react";
import { toast } from "sonner";

export default function PrivacySection() {
  const { t } = useI18n();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const isLocal = (user?.auth_provider || "local") === "local";
  const confirmExpected = "KONTO LOESCHEN";

  const exportData = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_BASE}/auth/data-export`, { credentials: "include" });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const fname = m?.[1] || "alles-geregelt-export.zip";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click();
      URL.revokeObjectURL(url); a.remove();
      toast.success(t.security.privacy.exportSuccess);
    } catch {
      toast.error(t.security.privacy.exportError);
    } finally {
      setExporting(false);
    }
  };

  const deleteAccount = async () => {
    if (confirmText.trim().toUpperCase() !== confirmExpected) {
      toast.error(t.security.privacy.confirmTypeWrong);
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/auth/account", { data: { password: pwd, confirmation: confirmExpected } });
      setUser(null);
      toast.success(t.security.privacy.deleteSuccess);
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, t.security.privacy.deleteError));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="ag-card p-7 space-y-7" data-testid="settings-privacy-section">
      <div className="flex items-center gap-2">
        <ShieldAlert className="h-4 w-4 text-slate-500" />
        <h2 className="font-serif text-2xl text-ag-ink">{t.security.privacy.title}</h2>
      </div>

      {/* Datenexport */}
      <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-start pb-6 border-b border-slate-100">
        <div>
          <div className="font-serif text-lg text-ag-ink inline-flex items-center gap-2">
            <FileArchive className="h-4 w-4 text-slate-400" /> {t.security.privacy.exportTitle}
          </div>
          <p className="text-sm text-slate-600 mt-1 max-w-lg leading-relaxed">{t.security.privacy.exportSub}</p>
        </div>
        <Button
          onClick={exportData} disabled={exporting}
          data-testid="settings-export"
          variant="outline" className="h-10 rounded-xl border-slate-200"
        >
          <Download className="h-4 w-4 mr-2" /> {exporting ? t.common.loading : t.security.privacy.exportBtn}
        </Button>
      </div>

      {/* Konto löschen */}
      <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-start">
        <div>
          <div className="font-serif text-lg text-red-700 inline-flex items-center gap-2">
            <Trash2 className="h-4 w-4" /> {t.security.privacy.deleteTitle}
          </div>
          <p className="text-sm text-slate-600 mt-1 max-w-lg leading-relaxed">{t.security.privacy.deleteSub}</p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          data-testid="settings-delete-account"
          variant="outline" className="h-10 rounded-xl border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
        >
          <Trash2 className="h-4 w-4 mr-2" /> {t.security.privacy.deleteBtn}
        </Button>
      </div>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="settings-delete-dialog" className="max-w-lg">
          <AlertDialogHeader>
            <div className="h-12 w-12 rounded-2xl bg-red-50 border border-red-200 flex items-center justify-center mb-2">
              <ShieldAlert className="h-5 w-5 text-red-700" />
            </div>
            <AlertDialogTitle className="font-serif text-2xl text-ag-ink">{t.security.privacy.deleteConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600 leading-relaxed">
              {t.security.privacy.deleteConfirmIntro}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <ul className="space-y-2 text-sm text-slate-700 my-2 pl-1">
            {t.security.privacy.deleteList.map((line, i) => (
              <li key={i} className="flex items-start gap-2">
                <Lock className="h-3.5 w-3.5 mt-1 text-slate-400 shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>

          <div className="space-y-3 pt-2">
            {isLocal && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t.security.profile.confirmPwd}</Label>
                <Input
                  type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                  data-testid="settings-delete-pwd" className="mt-1.5 h-11 bg-white"
                />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                {t.security.privacy.confirmTypeLabel(confirmExpected)}
              </Label>
              <Input
                value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                data-testid="settings-delete-confirm"
                placeholder={confirmExpected}
                className="mt-1.5 h-11 bg-white font-mono"
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setPwd(""); setConfirmText(""); }}>{t.common.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteAccount}
              data-testid="settings-delete-confirm-btn"
              disabled={deleting || confirmText.trim().toUpperCase() !== confirmExpected || (isLocal && !pwd)}
              className="bg-red-700 hover:bg-red-800 text-white"
            >
              {deleting ? t.common.loading : t.security.privacy.deleteFinal}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
